import { formatarPlaca, localDayKey } from '@locaveri/shared'
import { Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { CarFront } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'

import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type OcupacaoDoVeiculo, useVisaoDeOcupacao } from '@/features/bi/api'
import { BadgeDeStatusDoVeiculo } from '@/features/frota/badges'

/**
 * A OCUPAÇÃO DA FROTA — a tela da operação, sem nenhum valor em dinheiro.
 *
 * ===========================================================================
 * POR QUE ESTA TELA EXISTE SEPARADA DOS INDICADORES
 * ===========================================================================
 * A operação precisa saber qual carro ficou parado. A tela de Indicadores
 * responde isso — e responde junto a receita, o custo e a margem de cada carro.
 * O OPERACIONAL chegou a receber `report.read` por esse motivo e perdeu: o
 * CLAUDE.md define o papel como "sem financeiro". Esta tela lê
 * `GET /metrics/fleet-occupancy`, com chave própria (`fleet_occupancy.read`), e
 * a API nem consulta dinheiro para montá-la.
 *
 * ===========================================================================
 * "PARADO" É PRONTO E SEM LOCAÇÃO
 * ===========================================================================
 * Não é a ociosidade dos Indicadores (`posse − alugados`), que conta a oficina:
 * para o dono do ativo, o carro na funilaria também não rendeu. Para quem opera,
 * "parado" é o carro liberado que não saiu — é nele que vale procurar demanda.
 * A oficina aparece na coluna própria, ao lado, para as duas perguntas não se
 * misturarem. A conta vem pronta da API (`diasParadoDisponivel` no shared).
 *
 * ===========================================================================
 * `null` NÃO É ZERO — a mesma regra dos Indicadores
 * ===========================================================================
 * Ocupação `0%` = o carro estava disponível e não alugou. Traço = não dá para
 * responder (o carro não existia na janela, ou passou a janela inteira
 * bloqueado). Um `?? 0` aqui apagaria a diferença.
 */

/**
 * `AAAA-MM-DD` de `dias` atrás, no fuso da OPERAÇÃO.
 *
 * ⚠️ Não `toISOString().slice(0, 10)`: depois das 21h em São Paulo o dia UTC já
 * é o seguinte, e o filtro abriria com "até amanhã". É o mesmo defeito que já
 * apareceu na trilha de auditoria e no cupom.
 */
function diaLocalHa(dias: number): string {
  return localDayKey(new Date(Date.now() - dias * 86_400_000))
}

function taxa(valor: number | null): string {
  return valor === null ? '—' : `${String(valor)}%`
}

function Dias({
  valor,
  destaque = false,
}: {
  readonly valor: number
  readonly destaque?: boolean
}): ReactElement {
  return (
    <span
      className={
        destaque
          ? 'block text-right font-semibold tabular-nums text-foreground'
          : 'block text-right tabular-nums text-fg-secondary'
      }
    >
      {valor}
    </span>
  )
}

export function OcupacaoDaFrotaPage(): ReactElement {
  const [de, setDe] = useState(() => diaLocalHa(30))
  const [ate, setAte] = useState(() => diaLocalHa(0))
  const janelaValida = de !== '' && ate !== '' && de <= ate

  const consulta = useVisaoDeOcupacao({ de, ate })
  const dados = janelaValida ? consulta.data : undefined

  const colunas = useMemo<ColumnDef<OcupacaoDoVeiculo>[]>(
    () => [
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha) => linha.placa,
        cell: ({ row }) => (
          <div className="min-w-36">
            <Link
              to="/frota/$veiculoId"
              params={{ veiculoId: row.original.vehicleId }}
              className="font-medium tabular-nums text-foreground underline-offset-4 hover:underline"
            >
              {formatarPlaca(row.original.placa)}
            </Link>
            <p className="truncate text-xs text-fg-secondary">
              {row.original.modelo} · {row.original.categoriaNome}
            </p>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Agora',
        accessorFn: (linha) => linha.statusAtual,
        cell: ({ row }) => <BadgeDeStatusDoVeiculo status={row.original.statusAtual} />,
      },
      {
        id: 'parado',
        header: 'Parado',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha) => linha.diasParadoDisponivel,
        cell: ({ row }) => <Dias valor={row.original.diasParadoDisponivel} destaque />,
      },
      {
        id: 'alugado',
        header: 'Alugado',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha) => linha.diasAlugados,
        cell: ({ row }) => <Dias valor={row.original.diasAlugados} />,
      },
      {
        id: 'bloqueado',
        header: 'Bloqueado',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha) => linha.diasBloqueados,
        cell: ({ row }) => <Dias valor={row.original.diasBloqueados} />,
      },
      {
        id: 'ocupacao',
        header: 'Ocupação',
        meta: { alinhamento: 'direita' },
        // `null` vai para o fim em qualquer direção: "não dá para responder"
        // não é o menor nem o maior valor.
        accessorFn: (linha) => linha.taxaDeOcupacao ?? undefined,
        sortUndefined: 'last',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-foreground">
            {taxa(row.original.taxaDeOcupacao)}
          </span>
        ),
      },
      {
        id: 'km',
        header: 'Km',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha) => linha.kmRodado,
        cell: ({ row }) => (
          <span className="block text-right tabular-nums text-fg-secondary">
            {row.original.kmRodado.toLocaleString('pt-BR')}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 sm:p-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Ocupação da frota</h1>
        <p className="mt-1 max-w-3xl text-sm text-fg-secondary">
          Quantos dias cada carro ficou parado, alugado ou bloqueado no período. “Parado” é o carro
          pronto para sair que ninguém alugou; oficina, sinistro e limpeza contam como bloqueado.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="ocupacao-de">De</Label>
          <Input
            id="ocupacao-de"
            type="date"
            value={de}
            max={ate}
            onChange={(evento) => {
              setDe(evento.target.value)
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="ocupacao-ate">Até</Label>
          <Input
            id="ocupacao-ate"
            type="date"
            value={ate}
            min={de}
            onChange={(evento) => {
              setAte(evento.target.value)
            }}
          />
        </div>
      </div>

      {!janelaValida ? (
        <p className="text-sm text-danger-fg" role="alert">
          A data inicial precisa ser anterior ou igual à final.
        </p>
      ) : null}

      {dados === undefined ? null : (
        <Card>
          <CardContent className="grid gap-4 p-4 sm:grid-cols-4">
            <Resumo rotulo="Dias parado" valor={String(dados.total.diasParadoDisponivel)} />
            <Resumo rotulo="Dias alugado" valor={String(dados.total.diasAlugados)} />
            <Resumo rotulo="Dias bloqueado" valor={String(dados.total.diasBloqueados)} />
            <Resumo rotulo="Ocupação da frota" valor={taxa(dados.total.taxaDeOcupacao)} destaque />
          </CardContent>
        </Card>
      )}

      {/* ⚠️ ABAIXO DE `lg` NÃO HÁ TABELA. A primeira versão rolava a tabela
          dentro do contêiner, e em 375px aparecia só a coluna da placa: o
          número que a tela existe para mostrar ficava fora da vista, sem
          nenhuma pista de que havia mais à direita. Com `md` o mesmo corte
          voltava em 768px, onde a barra lateral come a largura das colunas. */}
      <div className="hidden overflow-x-auto lg:block">
        <TabelaDeDados
          colunas={colunas}
          dados={dados?.veiculos ?? []}
          carregando={janelaValida && consulta.isPending}
          erro={janelaValida ? consulta.error : null}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
          legenda="Ocupação por veículo no período, mais parados primeiro"
          ordenacaoInicial={[{ id: 'parado', desc: true }]}
          estadoVazio={<FrotaVazia />}
        />
      </div>

      <div className="lg:hidden">
        {!janelaValida ? null : consulta.isPending ? (
          <LoadingState rotulo="Carregando a ocupação…" linhas={4} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar a ocupação"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : (dados?.veiculos.length ?? 0) === 0 ? (
          <FrotaVazia />
        ) : (
          <ul
            className="grid gap-3 sm:grid-cols-2"
            aria-label="Ocupação por veículo, mais parados primeiro"
          >
            {[...(dados?.veiculos ?? [])]
              .sort((a, b) => b.diasParadoDisponivel - a.diasParadoDisponivel)
              .map((linha) => (
                <li key={linha.vehicleId}>
                  <CartaoDoVeiculo linha={linha} />
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function FrotaVazia(): ReactElement {
  return (
    <EmptyState
      icone={CarFront}
      titulo="Nenhum carro na frota neste período"
      descricao="Só entram carros que já tinham sido adquiridos e ainda não tinham sido baixados nas datas escolhidas."
    />
  )
}

/** A linha da tabela, dobrada para caber em 375px sem esconder número nenhum. */
function CartaoDoVeiculo({ linha }: { readonly linha: OcupacaoDoVeiculo }): ReactElement {
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link
              to="/frota/$veiculoId"
              params={{ veiculoId: linha.vehicleId }}
              className="font-medium tabular-nums text-foreground underline-offset-4 hover:underline"
            >
              {formatarPlaca(linha.placa)}
            </Link>
            <p className="truncate text-xs text-fg-secondary">
              {linha.modelo} · {linha.categoriaNome}
            </p>
          </div>
          <BadgeDeStatusDoVeiculo status={linha.statusAtual} />
        </div>
        <dl className="grid grid-cols-4 gap-2 text-center">
          <NumeroDoCartao rotulo="Parado" valor={String(linha.diasParadoDisponivel)} destaque />
          <NumeroDoCartao rotulo="Alugado" valor={String(linha.diasAlugados)} />
          <NumeroDoCartao rotulo="Bloqueado" valor={String(linha.diasBloqueados)} />
          <NumeroDoCartao rotulo="Ocupação" valor={taxa(linha.taxaDeOcupacao)} />
        </dl>
      </CardContent>
    </Card>
  )
}

function NumeroDoCartao({
  rotulo,
  valor,
  destaque = false,
}: {
  readonly rotulo: string
  readonly valor: string
  readonly destaque?: boolean
}): ReactElement {
  return (
    <div>
      <dt className="text-[11px] text-fg-muted">{rotulo}</dt>
      <dd
        className={
          destaque
            ? 'text-lg font-semibold tabular-nums text-foreground'
            : 'text-lg tabular-nums text-fg-secondary'
        }
      >
        {valor}
      </dd>
    </div>
  )
}

function Resumo({
  rotulo,
  valor,
  destaque = false,
}: {
  readonly rotulo: string
  readonly valor: string
  readonly destaque?: boolean
}): ReactElement {
  return (
    <div>
      <p className="text-xs text-fg-muted">{rotulo}</p>
      <p
        className={
          destaque
            ? 'mt-0.5 text-2xl font-semibold tabular-nums text-primary'
            : 'mt-0.5 text-2xl font-semibold tabular-nums text-foreground'
        }
      >
        {valor}
      </p>
    </div>
  )
}
